import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Space, Tooltip, message, Alert, Tabs } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCameraRetro } from "@fortawesome/free-solid-svg-icons";
import analyticsApi from "../../../api/analytics-api";
import AppGrowthSummaryCards from "./app-growth-summary-cards";
import AppGrowthCharts from "./app-growth-charts";
import AppGrowthTable from "./app-growth-table";
import AppGrowthExternalTab from "./app-growth-external-tab";

// Trang /app-growth — chỉ số tăng trưởng app, đọc từ bảng chụp AppGrowthStats.
//
// KHÔNG có đường nào ở đây query trực tiếp Orders (1,68M dòng). Query growth trên
// Orders mất 13–31 giây và một bản viết sai chiều CAST từng treo prod 395 giây.
// Số do cron 11:20 VN chụp mỗi đêm (backend/src/jobs/app-growth-snapshot-job.js).
//
// Nút máy ảnh: có chọn khoảng ngày → backfill khoảng đó; bỏ trống → chạy đúng lượt
// cron (8 ngày gần nhất). Đây là query NẶNG, đừng bấm liên tục.

const DEFAULT_DAYS = 90;

function AppGrowthPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snapshotting, setSnapshotting] = useState(false);
    const [range, setRange] = useState(null); // [dayjs, dayjs] | null
    // Nút backfill/chụp lại chỉ có nghĩa với tab Nội bộ → ẩn khi sang tab Nguồn ngoài
    // (nguồn ngoài do cron 11:40 VN kéo về, không chụp lại bằng tay được).
    const [tab, setTab] = useState("internal");

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await analyticsApi.getAppGrowthStats({ limit: DEFAULT_DAYS });
            setRows(res.data.data || []);
        } catch (err) {
            console.error("[AppGrowth] fetch error:", err);
            message.error("Không tải được số liệu growth");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSnapshot = async () => {
        setSnapshotting(true);
        try {
            if (range && range[0] && range[1]) {
                const from = range[0].format("YYYY-MM-DD");
                const to = range[1].format("YYYY-MM-DD");
                const res = await analyticsApi.runAppGrowthSnapshot({ from, to });
                message.success(`Đã chụp ${res.data.data?.ok}/${res.data.data?.days} ngày (${from} → ${to})`);
            } else {
                const res = await analyticsApi.runAppGrowthSnapshot();
                message.success(`Đã chụp lại ${res.data.data?.ok}/${res.data.data?.days} ngày gần nhất`);
            }
            fetchData();
        } catch (err) {
            console.error("[AppGrowth] snapshot error:", err);
            message.error("Chụp số liệu thất bại");
        } finally {
            setSnapshotting(false);
        }
    };

    // API trả mới-nhất-trước. Thẻ số cần dòng mới nhất; biểu đồ cần xưa→nay.
    const latest = rows[0] || null;
    const chronological = useMemo(() => [...rows].reverse(), [rows]);

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold m-0">Tăng trưởng App</h1>
                    <p className="text-xs text-gray-500 m-0">
                        Đơn xếp theo ngày ĐẶT (purchaseTime) — khác Dashboard (xếp theo ngày sàn duyệt).
                        {latest ? ` Chụp gần nhất: ${latest.stat_date}.` : ""}
                    </p>
                </div>
                <Space wrap style={{ display: tab === "internal" ? undefined : "none" }}>
                    <DatePicker.RangePicker
                        size="small"
                        value={range}
                        onChange={setRange}
                        placeholder={["Backfill từ", "đến"]}
                    />
                    <Tooltip title={range ? "Backfill khoảng ngày đã chọn (nặng)" : "Chụp lại 8 ngày gần nhất"}>
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faCameraRetro} />}
                            loading={snapshotting}
                            onClick={onSnapshot}
                        />
                    </Tooltip>
                    <Tooltip title="Làm mới">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                            loading={loading}
                            onClick={fetchData}
                        />
                    </Tooltip>
                </Space>
            </div>

            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    { key: "internal", label: "Nội bộ (DB)", children: renderInternal() },
                    // Tab này tự gọi API riêng và chỉ fetch khi được mở lần đầu.
                    { key: "external", label: "Nguồn ngoài (Store · Firebase)", children: <AppGrowthExternalTab /> },
                ]}
                destroyInactiveTabPane={false}
            />
        </div>
    );

    function renderInternal() {
        return (
            <div className="space-y-4">
            <Alert
                type="info"
                showIcon
                message="Cách đọc trang này"
                description={
                    <ul className="m-0 pl-4 text-xs">
                        <li>
                            <b>Tỷ trọng app</b> tính trên đơn ĐÃ GẮN KÊNH (app + web). Cột “Chưa gắn” là
                            mảng CTV rải link, nằm ngoài phễu app/web — gộp vào sẽ dìm tỷ trọng app từ
                            ~95% xuống ~74%.
                        </li>
                        <li>
                            Ô hiện <b>“—”</b> nghĩa là <b>không đo được cho ngày đó</b> (nhóm “sức khoẻ
                            tệp” chỉ chụp được tại thời điểm cron chạy), không phải bằng 0.
                        </li>
                        <li>
                            <b>3 ngày cuối</b> của cột “Đơn ≤72h” và <b>hoa hồng</b> của ngày gần đây còn
                            tăng: cohort chưa đủ 72h, và sàn còn duyệt đơn thêm.
                        </li>
                    </ul>
                }
            />

            <AppGrowthSummaryCards latest={latest} loading={loading} />
            <AppGrowthCharts rows={chronological} loading={loading} />
            <AppGrowthTable rows={rows} loading={loading} />
            </div>
        );
    }
}

export default AppGrowthPage;
